import { CommonModule } from '@angular/common';
import { Component, signal, ChangeDetectionStrategy } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { fieldSorter } from '@scandium-oy/ngx-scandium';
import { startOfToday } from 'date-fns';
import { Observable, combineLatest } from 'rxjs';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import { AppCommonModule } from '../common.module';
import { MonthSwitchComponent } from '../components/month-switch/month-switch.component';
import { NoResultsComponent } from '../components/no-results/no-results.component';
import { DashboardViewComponent } from '../components/views/dashboard/view-dashboard.component';
import { ContractorsService } from '../services/contractors.service';
import { LicensesService } from '../services/licenses.service';
import { UsersService } from '../services/users.service';
import { TextDialogComponent } from '../text-dialog/text-dialog.dialog';

interface UsageContractor {
  guid: string;
  name: string;
  count: number;
  price: number;
  total: number;
  users: string[];
  license: string;
}

@Component({
  selector: 'app-invoicing-page',
  templateUrl: './invoicing.page.html',
  styleUrls: ['./invoicing.page.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AppCommonModule,
    CommonModule,
    DashboardViewComponent,
    MonthSwitchComponent,
    NoResultsComponent,
  ],
})
export class InvoicingPage {

  currentDate = signal<Date>(startOfToday());

  private usages$: Observable<UsageContractor[]> = combineLatest([toObservable(this.currentDate), this.contractorsService.getList()]).pipe(
    map(([date, contractors]) => contractors.map((c) => {
      const license$ = this.licensesService.get(c.guid, date.getFullYear(), date.getMonth() + 1);
      return Object.assign({}, c, { license$: license$ });
    })),
    map((items) => {
      const obs = items.map((it) => it.license$);
      return combineLatest(obs);
    }),
    switchMap((licenses) => combineLatest([this.contractorsService.getList(), licenses])),
    map(([contractors, licenses]) => licenses.filter((l) => l != null).map((l) => {
      const contractor = contractors.find((c) => c.guid === l.contractor);
      const price = l.price ?? 0;
      return {
        guid: l.guid,
        name: contractor?.name,
        count: l.count,
        price: price,
        total: price * l.count,
        users: l.users,
        license: l.license,
      };
    }),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  paid$ = this.usages$.pipe(
    map((items) => items.filter((it) => it.license !== 'free')),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  free$ = this.usages$.pipe(
    map((items) => items.filter((it) => it.license === 'free')),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor(
    private contractorsService: ContractorsService,
    private licensesService: LicensesService,
    private modalCtrl: ModalController,
    private translate: TranslateService,
    private usersService: UsersService,
  ) { }

  changeMonth(newMonth: Date) {
    this.currentDate.set(newMonth);
  }

  openUsers(userGuids: string[]) {
    this.usersService.getByGuids(userGuids).pipe(
      take(1),
    ).subscribe((users) => {
      const text = users.sort(fieldSorter(['displayName'])).map((u) => `${u.displayName}<br/>`).join('<br/>');
      this.modalCtrl.create({ component: TextDialogComponent, componentProps: { title: this.translate.instant('nav.users'), text } }).then(async (m) => m.present());
    });
  }
}
